import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Detect whether a single-line, `truncate`d element is actually overflowing (its text is clipped). Pair it with a
 * {@link Tooltip} gated on `isTruncated` so the FULL text shows on hover/focus ONLY when it's truncated — the app's
 * standard for clipped labels (breadcrumb segments, the facility title, …). We never use the native `title` attribute:
 * it has a fixed, long browser delay and can't be styled.
 *
 * Pass the rendered text (or any value that changes with it) as `dep`: the measurement + ResizeObserver are set up
 * ONCE on mount and re-run only when `dep` changes (a new label at the same box size, which the observer wouldn't
 * catch) — NOT on every unrelated re-render (diff panel: a missing deps array reflowed + rebuilt the observer each
 * render, ×N for an N-segment breadcrumb). `measure` is also returned to re-check on pointer-enter / focus.
 */
export function useTruncated<T extends HTMLElement = HTMLElement>(dep?: unknown) {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    // +1 guards sub-pixel rounding (scrollWidth can read 1px over clientWidth on non-truncated text).
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, dep]);

  return { ref, isTruncated, measure };
}
